import {
  OrderSelectedDetailActions,
  RepeatOrderSelectedDetailActionTypes,
} from '@ordersnproducts/modules/new-repeat-order/actions/order-detail.actions';
import { OrderSelectedDetailActions as ModifyOrderSelectedDetailActions } from '@ordersnproducts/modules/new-modify-order/actions/order-detail.actions';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { OrderBuilderActions, RESET_BUILDER } from '../../../actions/order-builder.actions';
import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import { BucketActions, BucketsActionTypes } from '../actions/bucket-actions/bucket.action';
import {
  LocationSpecificationActions,
  LocationSpecificationActionTypes,
} from '../actions/location-specifications.actions';
import { RecipeFormValues } from '../models/form-values';
import { Order } from '@cemex-core/cmx-api/models/order-management/order';
import { UnitCode } from '../models/enums/units-code.enum';

export interface LocationSpecificationFormState {
  forms: RecipeFormValues[];
  invoiceLoaded: boolean;
  specialInstructionsRequest: string;
  purchaseOrderRequest: string;
  contactPersonFormConfig: FieldConfig[];
  isProcessingForms: boolean;
  showErrorsModal: boolean;
  allHaveError: boolean;
  bucketsToCancel: RecipeFormValues;
}

export const initialState: LocationSpecificationFormState = {
  forms: [],
  invoiceLoaded: false,
  specialInstructionsRequest: '',
  purchaseOrderRequest: '',
  contactPersonFormConfig: undefined,
  isProcessingForms: false,
  showErrorsModal: false,
  allHaveError: false,
  bucketsToCancel: undefined,
};

export let formCounter = -1;

/**
 * Adds an item to the recipe
 */
const makeRecipeItem = (values: { [key: string]: any }, orderId?: number): RecipeFormValues => {
  const id = ++formCounter;
  const result: RecipeFormValues = {
    id: id,
    values: { ...values },
  };

  if (orderId) {
    result.orderId = orderId;
  }

  return result;
};

/**
 * Returns an item seq num for the given item
 * If it has anticipated invoice it will return it's already defined seq num
 * If normal created item, will enerate it
 * @param item
 * @param index
 */
const makeSeqNum = (item: RecipeFormValues, index: number): number => {
  if (item.values[SpecFieldNames.CONTRACT_ANTICIPATED_INVOICE]) {
    return item.values[SpecFieldNames.CONTRACT_ANTICIPATED_INVOICE].itemCode;
  } else {
    return 10 * (index + 1);
  }
};

const loadAdditionalServices = (order: Order): { [key: string]: any } => {
  const additionalServices =
    order.orderProducts[0].itemProperties && order.orderProducts[0].itemProperties.additionalServices
      ? order.orderProducts[0].itemProperties.additionalServices
      : [];
  const result: { [key: string]: any } = {};
  additionalServices.forEach(service => {
    result[`${service.additionalServiceCode}_${service.additionalServiceId}`] = true;
  });
  return result;
};

export function locationSpecificationFormReducer(
  state = initialState,
  action:
    | LocationSpecificationActions
    | OrderBuilderActions
    | BucketActions
    | OrderSelectedDetailActions
    | ModifyOrderSelectedDetailActions,
): LocationSpecificationFormState {
  switch (action.type) {
    case RESET_BUILDER:
    case LocationSpecificationActionTypes.RESET: {
      formCounter = -1;
      return {
        ...initialState,
      };
    }
    case LocationSpecificationActionTypes.ADD_ITEM: {
      return {
        ...state,
        forms: [...state.forms, makeRecipeItem({})],
      };
    }
    case LocationSpecificationActionTypes.DUPLICATE_ITEM: {
      const duplicatedItem = {
        id: formCounter,
        values: { ...action.payload, id: formCounter },
      };
      return {
        ...state,
        forms: [...state.forms, duplicatedItem],
      };
    }
    case LocationSpecificationActionTypes.UPDATE_ITEM: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.recipeFromValues.id) {
            const quantity = action.payload.recipeFromValues.values[SpecFieldNames.QUANTITY];
            return {
              ...action.payload.recipeFromValues,
              values: {
                ...action.payload.recipeFromValues.values,
                [SpecFieldNames.QUANTITY]: quantity && typeof quantity === 'number' && +quantity.toFixed(6),
              },
              contracts: item.contracts,
              units: item.units,
              spacing: item.spacing,
              additionalServices: item.additionalServices,
              orderId: item.orderId,
              locked: item.locked,
              loadingInvoice: item.loadingInvoice,
              invokeDC: item.invokeDC,
              dirty: item.dirty,
              dcValidation: item.dcValidation,
              plants: item.plants,
              isValid: item.isValid,
              hasError: item.hasError,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.RESET_DATE_TIME: {
      const newFormState = state.forms;
      newFormState.forEach(form => {
        form.values.time = null;
        if (form.values.hasOwnProperty(SpecFieldNames.TIME_TO)) {
          form.values[SpecFieldNames.TIME_TO] = undefined;
        }
        if (form.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS)) {
          form.values[SpecFieldNames.COUNTER_OFFERS] = undefined;
        }
        if (form.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS_TO)) {
          form.values[SpecFieldNames.COUNTER_OFFERS_TO] = undefined;
        }
        form.dcValidation = undefined;
        form.locked = false;
      });

      return { ...state, forms: newFormState };
    }
    case BucketsActionTypes.ADD_BUCKETS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              orderId: action.payload.orderId,
              spacing: action.payload.buckets,
              loadingLoads: false,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case BucketsActionTypes.SET_BUCKET: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.recipe.id) {
            return {
              ...item,
              spacing: item.spacing.map(load => {
                if (load.id === action.payload.bucket.id) {
                  load.values[SpecFieldNames.SPACING_TIME];
                  load.values[SpecFieldNames.SPACING_LOADS];
                  return load;
                } else {
                  return load;
                }
              }),
            };
          } else {
            return item;
          }
        }),
      };
    }
    case BucketsActionTypes.CLEAR_BUCKETS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              spacing: [],
            };
          } else {
            return item;
          }
        }),
      };
    }
    case BucketsActionTypes.FETCH_BUCKETS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              loadingLoads: true,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_ITEMS_SEQ_NUM: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          return { ...item, values: { ...item.values, [SpecFieldNames.ITEM_SEQ_NUM]: makeSeqNum(item, index) } };
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_INVOICE_LOADED: {
      return {
        ...state,
        invoiceLoaded: action.payload,
      };
    }
    case LocationSpecificationActionTypes.RESET_ITEMS_SEQ_NUM: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          return { ...item, values: { ...item.values, [SpecFieldNames.ITEM_SEQ_NUM]: undefined } };
        }),
      };
    }
    case LocationSpecificationActionTypes.REMOVE_ITEM: {
      return {
        ...state,
        forms: state.forms.filter(i => i.id !== action.payload),
      };
    }
    case LocationSpecificationActionTypes.STORE_ITEM_CONTRACTS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderId) {
            item.contracts = action.payload.contracts;
          }
          return item;
        }),
      };
    }
    case LocationSpecificationActionTypes.LOCK_ITEM: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              locked: action.payload.locked,
              loadingInvoice: action.payload.loadingInvoice,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_PROCESSING_FORM: {
      return {
        ...state,
        isProcessingForms: action.payload.locked,
      };
    }
    case LocationSpecificationActionTypes.SET_ERRORS_MODAL: {
      if (action.payload.allHaveError !== undefined) {
        return {
          ...state,
          showErrorsModal: action.payload.showErrorModal,
          allHaveError: action.payload.allHaveError,
        };
      } else {
        return {
          ...state,
          showErrorsModal: action.payload.showErrorModal,
        };
      }
    }
    case LocationSpecificationActionTypes.SET_FOR_DC_VALIDATION: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            if (item.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS) && action.payload.invokeDC) {
              item.values[SpecFieldNames.COUNTER_OFFERS] = undefined;
              if (item.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS_TO)) {
                item.values[SpecFieldNames.COUNTER_OFFERS_TO] = undefined;
              }
            }
            if (item.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS) && action.payload.invokeDC) {
              item.values[SpecFieldNames.COUNTER_OFFERS] = undefined;
            }
            return {
              ...item,
              values: {
                ...item.values,
                [SpecFieldNames.TIME]: action.payload.resetTime ? undefined : item.values[SpecFieldNames.TIME],
                [SpecFieldNames.TIME_TO]:
                  action.payload.resetTime || !item.values[SpecFieldNames.TIME_TO]
                    ? undefined
                    : item.values[SpecFieldNames.TIME_TO],
              },
              invokeDC: action.payload.invokeDC,
              dcValidation: action.payload.invokeDC ? undefined : item.dcValidation,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_DIRTY: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId || action.payload.dirtyAll) {
            return {
              ...item,
              dirty: action.payload.dirtyAll ? true : action.payload.dirty,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_ERROR_ON_VALIDATION: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              hasError: action.payload.hasError,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_FORM_UPDATED_TIME: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              updateTime: action.payload.updatedTime,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_INITIAL_LOAD: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId || action.payload.isInitialLoadAll) {
            return {
              ...item,
              isInitialLoad: true,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case LocationSpecificationActionTypes.SET_DC_VALIDATION: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              plant: _safe(() => action.payload.DCResponse.loads[0].plant),
              dcValidation: action.payload.DCResponse,
            };
          } else {
            return item;
          }
        }),
      };
    }

    case LocationSpecificationActionTypes.SET_FORM_VALID: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId || action.payload.formValidAll !== undefined) {
            return {
              ...item,
              values: {
                ...item.values,
              },
              isValid: action.payload.formValidAll ? action.payload.formValidAll : action.payload.formValid,
            };
          } else {
            return item;
          }
        }),
      };
    }

    case LocationSpecificationActionTypes.RESET_JOBSITE_SELECTION: {
      const newFormState = state.forms;
      newFormState.forEach(form => {
        form.values.unit = undefined;
        form.values.productSelect = undefined;
        form.values.contractSelect = undefined;
        if (form.orderId) {
          form.invokeDC = true;
          form.spacing = [];
          form.dcValidation = undefined;
          if (form.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS)) {
            form.values[SpecFieldNames.COUNTER_OFFERS] = undefined;
          }
          if (form.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS_TO)) {
            form.values[SpecFieldNames.COUNTER_OFFERS_TO] = undefined;
          }
        }
      });
      return { ...state, forms: newFormState };
    }

    case RepeatOrderSelectedDetailActionTypes.LOAD_ONLINE: {
      const contract = action.payload.hasOwnProperty('agreement')
        ? action.payload.agreement.agreementCode
        : action.payload.orderProducts[0].hasOwnProperty('contract')
        ? action.payload.orderProducts[0].contract.contractCode
        : undefined;
      const orderProduct = action.payload.orderProducts[0];
      const customerUnit = orderProduct.customerUnit;
      const unit = {
        unitId: _safe(() => customerUnit.unitId) || _safe(() => orderProduct.unitOfMeasureId),
        unitCode: _safe(() => customerUnit.customerUnitCode) || _safe(() => orderProduct.unitOfMeasure),
        unitDesc: _safe(() => customerUnit.customerUnitDesc) || _safe(() => orderProduct['unitOfMeasureDesc']),
      };
      const quantity =
        unit.unitCode === UnitCode.load
          ? _safe(() => action.payload['totalQuantityLoads'])
          : action.payload.totalQuantity;
      const values = {
        productSelect: action.payload.orderProducts[0].productId,
        catalog: false,
        quantity: quantity,
        unit: unit,
        contractSelect: contract,
        purchaseOrder: action.payload.purchaseOrder,
        additionalServices: loadAdditionalServices(action.payload),
        additionalInfo: action.payload.instructions,
        isRepeat: true,
      };

      return {
        ...state,
        forms: [makeRecipeItem(values)],
      };
    }

    case LocationSpecificationActionTypes.SET_RECEIVING_PERSON:
      state.forms.forEach(form => {
        if (form.locationConfig) {
          form.locationConfig.receivingPerson.selectedContactPersonId = action.payload;
        }
      });
      return state;

    case LocationSpecificationActionTypes.SET_PURCHASE_ORDER_REQUEST: {
      return {
        ...state,
        purchaseOrderRequest: action.payload,
      };
    }

    case LocationSpecificationActionTypes.SET_SPECIAL_INSTRUCTIONS_REQUEST: {
      return {
        ...state,
        specialInstructionsRequest: action.payload,
      };
    }

    case LocationSpecificationActionTypes.SET_CONTACT_PERSON_FORM_CONFIG: {
      return {
        ...state,
        contactPersonFormConfig: action.payload,
      };
    }

    case LocationSpecificationActionTypes.FETCH_ITEM_CONTRACTS:
    default: {
      return state;
    }

    case LocationSpecificationActionTypes.SET_ORDER_ID: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              orderId: action.payload.orderId,
            };
          } else {
            return item;
          }
        }),
      };
    }

    case LocationSpecificationActionTypes.SET_CATALOG_FALSE: {
      state.forms.forEach(form => {
        form.values.catalog = false;
      });
      return {
        ...state,
      };
    }

    case LocationSpecificationActionTypes.SET_LOCATION_SPECIFICATION_FROM_MODIFY: {
      const formState = action.payload.modifyFormState;
      return {
        ...formState,
      } as LocationSpecificationFormState;
    }
    case BucketsActionTypes.UPDATE_BUCKETS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId && !!item.spacing) {
            item.spacing.map(spacing => {
              if (spacing.id !== action.payload.itemId) {
                spacing.id = action.payload.itemId;
                spacing.values['itemId'] = action.payload.itemId;
              }
            });
            return item;
          } else {
            return item;
          }
        }),
      };
    }
  }
}
